import { useContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { sentryIdentify } from 'sentry/sentryError'
import {
  hasActiveMembership,
  hasSuspendedMembership,
  hasDelayedMembership,
  getMembershipStatus
} from 'utils'
import { MeContext } from 'contexts/me'
import {
  getCard,
  getLimit,
  setActivationCodePhysicalCard
} from 'api/credit-card'
import { useLimitPhysicalCardStore } from 'store'
import { MembershipCard } from 'components/MembershipCard'
import CreditCard from 'pages/myCard/components/CreditCard'
import SuspendedMembership from 'components/SuspendedMembership'
import WORDING from 'constants/wording'
import { MEMBERSHIP_STATUS } from 'constants/subscriptions'
import Message from './Message'
import PauseToggle from './PauseToggle'
import AuxModals from './AuxModals'
import MembershipLegend from './MembershipLegend'
import CardButtons from './CardButtons'

const { SUSPENDED_MEMBERSHIP_PHYSICAL_CARD } = WORDING
const { ACTIVE } = MEMBERSHIP_STATUS

const PhysicalCardTab = () => {
  // card data
  const [card, setCard] = useState(null)
  // if is getting card data
  const [loadingStatus, setLoadingStatus] = useState(false)
  // loading first render
  const [loadingPage, setLoadingPage] = useState(true)

  const { me } = useContext(MeContext)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const { updateInfo } = useLimitPhysicalCardStore()

  useEffect(() => {
    getCardData()
  }, [])

  useEffect(() => {
    if (!card) return
    const param = searchParams.get('ac')

    if (param) {
      setActivationCodePhysicalCard({
        card_uuid: card?.uuid,
        card_activation_code: param
      }).then(() => {
        searchParams.delete('ac')
        setSearchParams(searchParams)
        getCardData()
      })
    }
  }, [card])

  async function getCardData() {
    if (loadingStatus) return
    setLoadingStatus(true)

    try {
      const res = await getCard({ type: 'PHYSICAL' })

      if (res.success) {
        setCard(res.data)

        const userHasNoCard =
          Object.keys(res.data).length === 0 ||
          res.data?.card_status === 'DISABLED'

        if (userHasNoCard) {
          const limitRes = await getLimit()

          if (limitRes.success) {
            const { status: canRequestCard, cardLimit: minBalance } =
              limitRes.data.result
            updateInfo({ canRequestCard, minBalance })
          }
        }
      }
    } catch (error) {
      sentryIdentify(error)
    } finally {
      setLoadingStatus(false)
      setLoadingPage(false)
    }
  }

  // DERIVED STATES:
  const cardStatusError =
    card?.card_status === 'ERROR_CREATION' ||
    card?.card_status === 'ERROR_ASSOCIATION'

  const cardIsDisabled =
    card?.card_status === 'PENDING' ||
    card?.card_status !== 'ACTIVE' ||
    Boolean(card?.error) ||
    card?.card_status === 'BLOCKED' ||
    card?.card_status === 'DISABLED' ||
    (card !== null && Object.keys(card).length === 0)

  const showSwitch =
    !card?.error &&
    !loadingStatus &&
    (card?.card_status === 'ACTIVE' || card?.card_status === 'BLOCKED')

  const cardWasNotDelivered =
    card?.shipping?.status === 'NOT_DELIVERED' &&
    card?.card_status === 'DISABLED'

  const neitherMembershipNorCard =
    ((card !== null && Object.keys(card).length === 0) ||
      card?.card_status === 'DISABLED') &&
    !hasActiveMembership(me?.subscriber_products) &&
    !hasSuspendedMembership(me?.subscriber_products) &&
    !loadingStatus

  const noCardAndSuspendedMembership =
    ((card !== null && Object.keys(card).length === 0) ||
      card?.card_status === 'DISABLED' ||
      cardWasNotDelivered) &&
    (hasSuspendedMembership(me?.subscriber_products) ||
      hasDelayedMembership(me?.subscriber_products))

  // COMPONENT RETURNS
  if (loadingPage || !me || !card) {
    return <span />
  }

  if (cardStatusError) {
    return <AuxModals type='CONTACT_ADVISOR' />
  }

  if (
    cardWasNotDelivered &&
    getMembershipStatus(me?.subscriber_products) === ACTIVE
  ) {
    return <AuxModals type='PHYSICAL_CARD_NOT_DELIVERED' />
  }

  if (neitherMembershipNorCard) {
    return (
      <MembershipCard
        primarybuttonFunc={() => navigate('/suscripcion/tarjeta-fisica')}
      />
    )
  }

  if (noCardAndSuspendedMembership) {
    return (
      <SuspendedMembership
        bigIcon
        className='border border-red-500 rounded-[10px] p-3 h-[250px] flex-col justify-around text-center'
        paragraphText={SUSPENDED_MEMBERSHIP_PHYSICAL_CARD}
        eventName='bille_boton_tarjeta_fisica_membresia_suspendida_pagar'
        title={
          hasDelayedMembership(me?.subscriber_products)
            ? 'Tenés una cuota demorada de tu membresía.'
            : null
        }
      />
    )
  }

  return (
    <div>
      <div className='w-full md:gap-3 md:grid md:grid-cols-2'>
        {/* TARJETA */}
        <CreditCard
          setLoading={setLoadingStatus}
          disabled={cardIsDisabled}
          loading={loadingStatus}
          card={card}
        />
        {/* SWITCH */}
        {showSwitch && (
          <PauseToggle
            loadingStatus={loadingStatus}
            card={card}
            getCard={getCardData}
          />
        )}
      </div>

      {/* MESSAGES */}
      {/* Blocked card message */}
      {card?.card_status === 'DISABLED' && !loadingStatus && (
        <div className='w-full mt-8'>
          <Message
            className='bg-blue-900 h-12 py-4'
            text='Su tarjeta fue bloqueada'
          />
        </div>
      )}

      {/* BOTONES */}
      <CardButtons typeCard='PHYSICAL' card={card} getCardData={getCardData} />

      {/* LEYENDA */}
      <MembershipLegend />
    </div>
  )
}

export default PhysicalCardTab
